import { useState, useCallback, useRef, useEffect } from 'react';
import SignaturePad from 'signature_pad';
import type { ISignatureInputProps } from '../../../SignatureInput.types';
import { debounce } from '@wix/editor-elements-common-utils';
import { useDomDirection } from '@wix/editor-elements-common-utils/hooks';
import { calculatePenColor, transformPenSizeToWidths } from '../../utils';
import type { SignatureInputMode } from './types';
import { useResizeObserver } from '../../../../../providers/useResizeObserver';

const RESIZE_OBSERVER_DELAY = 300;
const TEXT_PADDING = 40;
const CURSOR_MARGIN = 20;

export const useSignatureInput = (props: ISignatureInputProps) => {
  const {
    id,
    onChange,
    onValueChange,
    onFocus,
    onBlur,
    value,
    signatureColor,
    signatureSize,
    isDisabled,
    validateValueAndShowIndication,
    hideValidityIndication,
  } = props;

  const [isDrawing, setIsDrawing] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [a11yValue, setA11yValue] = useState<string>(value);
  const signaturePadRef = useRef<SignaturePad | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const padContainerRef = useRef<HTMLDivElement>(null);
  const resizeInProgress = useRef<boolean>(false);
  const { direction, directionRef } = useDomDirection<HTMLDivElement>();

  const [selectedMode, setSelectedMode] = useState<SignatureInputMode>('type');
  const [drawnSignatureData, setDrawnSignatureData] = useState('');
  const [typedSignatureData, setTypedSignatureData] = useState('');
  const typedSignatureRef = useRef('');
  const cursorPositionRef = useRef(0);
  const cursorBlinkIntervalRef = useRef<number | null>(null);
  const applySignatureTextStyle = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      ctx.font = '25px "Sacramento"';
      ctx.fillStyle = calculatePenColor(signatureColor);
    },
    [signatureColor],
  );

  const getCanvasMetrics = useCallback(() => {
    const ratio = window.devicePixelRatio || 1;
    if (!canvasRef.current) {
      return { ratio, canvasWidth: 0, canvasHeight: 0 };
    }
    return {
      ratio,
      canvasWidth: canvasRef.current.width / ratio,
      canvasHeight: canvasRef.current.height / ratio,
    };
  }, []);

  const getTextPosition = useCallback(() => {
    const { canvasHeight, canvasWidth } = getCanvasMetrics();
    const textX =
      direction === 'rtl' ? canvasWidth - TEXT_PADDING : TEXT_PADDING;
    const textY = canvasHeight / 2;
    return { textX, textY };
  }, [direction, getCanvasMetrics]);

  const renderTypedSignature = useCallback(
    (text: string, saveData = true) => {
      if (!canvasRef.current || !signaturePadRef.current) {
        return '';
      }
      const ctx = canvasRef.current.getContext('2d');
      if (!ctx) {
        return '';
      }

      signaturePadRef.current.clear();
      if (!text) {
        if (saveData) {
          setTypedSignatureData('');
        }
        return '';
      }
      applySignatureTextStyle(ctx);
      ctx.textBaseline = 'middle';
      ctx.textAlign = direction === 'rtl' ? 'right' : 'left';
      const { textX, textY } = getTextPosition();
      ctx.fillText(text, textX, textY);
      if (saveData) {
        const newData = canvasRef.current.toDataURL();
        setTypedSignatureData(newData);
        return newData;
      }

      return '';
    },
    [direction, applySignatureTextStyle, getTextPosition],
  );

  const drawCursorAtPosition = useCallback(() => {
    if (!canvasRef.current) {
      return;
    }
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) {
      return;
    }

    renderTypedSignature(typedSignatureRef.current, false);
    applySignatureTextStyle(ctx);
    const { textX, textY } = getTextPosition();
    const textBeforeCursor = typedSignatureRef.current.substring(
      0,
      cursorPositionRef.current,
    );
    const measure = ctx.measureText(textBeforeCursor).width;
    const cursorX = direction === 'rtl' ? textX - measure : textX + measure;
    ctx.beginPath();
    ctx.moveTo(cursorX, textY - CURSOR_MARGIN);
    ctx.lineTo(cursorX, textY + CURSOR_MARGIN);
    ctx.lineWidth = 1;
    ctx.stroke();
  }, [
    direction,
    renderTypedSignature,
    applySignatureTextStyle,
    getTextPosition,
  ]);

  const updateCanvasDimensionsAndScale = useCallback(() => {
    if (resizeInProgress.current) {
      return;
    }
    resizeInProgress.current = true;
    if (canvasRef.current) {
      const { ratio } = getCanvasMetrics();
      const canvas = canvasRef.current;
      const currentData =
        selectedMode === 'draw'
          ? drawnSignatureData
          : typedSignatureRef.current;
      canvas.width = canvas.clientWidth * ratio;
      canvas.height = canvas.clientHeight * ratio;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.scale(ratio, ratio);
      }

      if (currentData) {
        const img = new Image();
        img.onload = () => {
          ctx?.drawImage(
            img,
            0,
            0,
            canvas.width / ratio,
            canvas.height / ratio,
          );
          resizeInProgress.current = false;
          if (selectedMode === 'type' && isFocused) {
            drawCursorAtPosition();
          }
        };
        img.src = currentData;
      } else if (selectedMode === 'type' && typedSignatureRef.current) {
        renderTypedSignature(typedSignatureRef.current);
        resizeInProgress.current = false;
        if (isFocused) {
          drawCursorAtPosition();
        }
      } else {
        resizeInProgress.current = false;
      }
    } else {
      resizeInProgress.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedMode,
    drawnSignatureData,
    typedSignatureData,
    isFocused,
    renderTypedSignature,
    drawCursorAtPosition,
  ]);

  const resizeObserverCallback = useCallback(() => {
    debounce(updateCanvasDimensionsAndScale, RESIZE_OBSERVER_DELAY)();
  }, [updateCanvasDimensionsAndScale]);

  useResizeObserver({
    ref: padContainerRef,
    callback: resizeObserverCallback,
  });

  const onDrawStart = useCallback(() => {
    if (selectedMode === 'draw') {
      setIsDrawing(true);
    }
  }, [selectedMode]);

  const onDrawEnd = useCallback(() => {
    if (selectedMode !== 'draw') {
      return;
    }
    setIsDrawing(false);
    const newValue = signaturePadRef.current?.toDataURL() || '';
    setDrawnSignatureData(newValue);
    if (value !== newValue) {
      onValueChange(newValue);
      validateValueAndShowIndication?.();
      onChange({
        type: 'change',
        compId: id,
        target: { value: newValue },
      });
    }
  }, [
    selectedMode,
    id,
    onChange,
    onValueChange,
    validateValueAndShowIndication,
    value,
  ]);

  useEffect(() => {
    if (canvasRef.current) {
      if (!signaturePadRef.current) {
        signaturePadRef.current = new SignaturePad(canvasRef.current, {
          onBegin: onDrawStart,
          onEnd: onDrawEnd,
        });
        updateCanvasDimensionsAndScale();
      } else {
        signaturePadRef.current.onBegin = onDrawStart;
        signaturePadRef.current.onEnd = onDrawEnd;
      }
    }
  }, [onDrawEnd, onDrawStart, updateCanvasDimensionsAndScale]);

  useEffect(() => {
    return () => {
      signaturePadRef.current?.off();
      if (cursorBlinkIntervalRef.current) {
        window.clearInterval(cursorBlinkIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const { minWidth, maxWidth } = transformPenSizeToWidths(signatureSize);
    if (signaturePadRef.current) {
      signaturePadRef.current.minWidth = minWidth;
      signaturePadRef.current.maxWidth = maxWidth;
    }
  }, [signatureSize]);

  useEffect(() => {
    if (signaturePadRef.current) {
      signaturePadRef.current.penColor = calculatePenColor(signatureColor);
    }
  }, [signatureColor]);

  useEffect(() => {
    if (signaturePadRef.current) {
      if (isDisabled) {
        signaturePadRef.current.off();
      } else if (selectedMode === 'draw') {
        signaturePadRef.current.on();
      } else {
        signaturePadRef.current.off();
      }
    }
  }, [isDisabled, selectedMode]);

  useEffect(() => {
    if (!signaturePadRef.current || !canvasRef.current) {
      return;
    }

    const { canvasHeight, canvasWidth } = getCanvasMetrics();

    const ctx = canvasRef.current.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);
    }
    if (selectedMode === 'type') {
      signaturePadRef.current.off();

      if (typedSignatureData) {
        const img = new Image();
        img.onload = () => {
          if (ctx && canvasRef.current) {
            ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);
            if (isFocused) {
              drawCursorAtPosition();
            }
          }
        };
        img.src = typedSignatureData;
      } else if (typedSignatureRef.current) {
        renderTypedSignature(typedSignatureRef.current);
        if (isFocused) {
          drawCursorAtPosition();
        }
      }
    } else {
      if (!isDisabled) {
        signaturePadRef.current.on();
      }
      if (cursorBlinkIntervalRef.current) {
        window.clearInterval(cursorBlinkIntervalRef.current);
        cursorBlinkIntervalRef.current = null;
      }
      if (drawnSignatureData) {
        const img = new Image();
        img.onload = () => {
          if (ctx && canvasRef.current) {
            ctx.clearRect(0, 0, canvasWidth, canvasHeight);
            ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);
          }
        };
        img.src = drawnSignatureData;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedMode,
    typedSignatureData,
    drawnSignatureData,
    isDisabled,
    isFocused,
    drawCursorAtPosition,
    renderTypedSignature,
  ]);

  useEffect(() => {
    if (selectedMode === 'type' && isFocused) {
      let cursorVisible = true;
      drawCursorAtPosition();
      cursorBlinkIntervalRef.current = window.setInterval(() => {
        if (canvasRef.current && isFocused) {
          if (cursorVisible) {
            renderTypedSignature(typedSignatureRef.current, false);
          } else {
            drawCursorAtPosition();
          }
          cursorVisible = !cursorVisible;
        }
      }, 500);
    }

    return () => {
      if (cursorBlinkIntervalRef.current) {
        window.clearInterval(cursorBlinkIntervalRef.current);
        cursorBlinkIntervalRef.current = null;
      }
    };
  }, [selectedMode, isFocused, drawCursorAtPosition, renderTypedSignature]);

  const blurTypeMode = () => {
    setIsFocused(false);
    if (cursorBlinkIntervalRef.current) {
      window.clearInterval(cursorBlinkIntervalRef.current);
      cursorBlinkIntervalRef.current = null;
    }

    renderTypedSignature(typedSignatureRef.current);
    inputRef.current?.blur();
  };

  const focusTypeMode = () => {
    if (isDisabled) {
      return;
    }

    setIsFocused(true);
    cursorPositionRef.current = typedSignatureRef.current.length;
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (selectedMode !== 'type' || isDisabled || !isFocused) {
      return;
    }
    let newText = typedSignatureRef.current;
    let newPos = cursorPositionRef.current;
    let shouldUpdate = false;

    switch (e.key) {
      case 'Backspace':
        if (newPos > 0) {
          newText = newText.slice(0, newPos - 1) + newText.slice(newPos);
          newPos--;
          shouldUpdate = true;
        }
        break;
      case 'Delete':
        if (newPos < newText.length) {
          newText = newText.slice(0, newPos) + newText.slice(newPos + 1);
          shouldUpdate = true;
        }
        break;
      case 'ArrowLeft':
        newPos = Math.max(0, newPos - 1);
        break;
      case 'ArrowRight':
        newPos = Math.min(newText.length, newPos + 1);
        break;
      case 'Home':
        newPos = 0;
        break;
      case 'End':
        newPos = newText.length;
        break;
      default:
        if (e.key.length === 1) {
          newText = newText.slice(0, newPos) + e.key + newText.slice(newPos);
          newPos++;
          shouldUpdate = true;
        }
    }
    typedSignatureRef.current = newText;
    cursorPositionRef.current = newPos;

    if (inputRef.current) {
      inputRef.current.value = newText;
    }
    if (shouldUpdate) {
      drawCursorAtPosition();
      const newValue = renderTypedSignature(newText);
      if (newValue && value !== newValue) {
        onValueChange(newValue);
        validateValueAndShowIndication?.();
        onChange({
          type: 'change',
          compId: id,
          target: { value: newValue },
        });
      }
    } else {
      drawCursorAtPosition();
    }
  };

  const handleTypedSignatureChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newText = e.target.value;
    typedSignatureRef.current = newText;
    cursorPositionRef.current = newText.length;
    setA11yValue(newText);

    const newValue = renderTypedSignature(newText);
    drawCursorAtPosition();

    if (newValue && value !== newValue) {
      onValueChange(newValue);
      validateValueAndShowIndication?.();
      onChange({
        type: 'change',
        compId: id,
        target: { value: newValue },
      });
    }
  };

  const _onBlur: React.FocusEventHandler = event => {
    if (selectedMode === 'type') {
      blurTypeMode();
    }

    onBlur?.(event);
  };

  const _onFocus: React.FocusEventHandler = event => {
    setTimeout(() => {
      if (document.activeElement === canvasRef.current) {
        if (selectedMode === 'type' && !isDisabled) {
          setIsFocused(true);
          focusTypeMode();
          onFocus?.(event);
        }
      }
    }, 0);
  };

  const handleRadioChange = (mode: SignatureInputMode) => {
    setSelectedMode(mode);

    const currentValue =
      mode === 'draw' ? drawnSignatureData : typedSignatureData;

    onValueChange(currentValue || '');
    onChange({
      type: 'change',
      compId: id,
      target: { value: currentValue || '' },
    });
  };

  const handleClear = ({
    shouldTriggerChangeEvent,
  }: {
    shouldTriggerChangeEvent: boolean;
  }) => {
    if (signaturePadRef.current && value) {
      signaturePadRef.current.clear();
      if (selectedMode === 'type') {
        typedSignatureRef.current = '';
        setTypedSignatureData('');
        cursorPositionRef.current = 0;

        if (inputRef.current) {
          inputRef.current.value = '';
        }
      } else {
        setDrawnSignatureData('');
      }

      setA11yValue('');
      onValueChange('');
      validateValueAndShowIndication?.();

      if (shouldTriggerChangeEvent) {
        onChange({
          type: 'change',
          compId: id,
          target: { value: '' },
        });
      }
    }
    hideValidityIndication?.();
  };

  return {
    padContainerRef,
    canvasRef,
    inputRef,
    directionRef,
    a11yValue,
    isDrawing,
    isFocused,
    selectedMode,
    focusTypeMode,
    blurTypeMode,
    handleRadioChange,
    handleKeyDown,
    handleClear,
    _onFocus,
    _onBlur,
    handleTypedSignatureChange,
  };
};
